// Growem - marcas del grafico de fuerza (logica pura, sin JSX).
//
// POR QUE ESTE MODULO: Resumen.tsx dibuja el indice de fuerza, pero TODO lo que decide QUE marca
// lleva cada punto (relleno o hueco, con anillo, con o sin marcador), donde va cada glifo de
// cambio de canasta y que texto lo explica es logica de dominio, no de render. Vivir en src/lib
// (patron del repo: modulo puro + su .test.ts al lado) permite probar las reglas sin montar React
// y garantiza que la leyenda y el trazo nunca puedan divergir: ambos leen de aqui.
//
// CONTRATO CON EL NUCLEO (src/lib/strength.ts, recalibracion 2026-08):
//  - IndexPoint.obs   = miembros que VOTARON en el paso que produjo el punto (0 en el primer punto
//                       de la serie, que es la base 100 y no viene de ningun paso).
//  - IndexPoint.lowConf     = ese dia hubo una medicion poco fiable (fuera del rango util de Epley).
//  - IndexPoint.provisional = el paso venia fuera de rango y se recorto a +-ln(1.30).
//  - ExercisePoint.rirImputed = el esfuerzo de la serie tope no estaba anotado y se estimo.
//  - STALE_DAYS = dias sin medicion real tras los cuales un miembro sale de la canasta. Es la UNICA
//    regla del nucleo que este modulo REPLICA (no puede importarse la funcion interna): basketEvents
//    la usa para anotar retiros y re-entradas, y el test de borde 45/46 la mantiene sincronizada.
// Fuera de eso este modulo NO recalcula nada: solo lo traduce a marcas y a frases en espanol llano.

import { LINE_COLORS } from '../data/plan'
import { parseDay } from '../domain/dates'
import { STALE_DAYS, type GroupIndex } from './strength'

/** Gris neutro de contexto: lineas atenuadas, reglas verticales y desbordes de paleta. */
export const DIM_COLOR = 'var(--ink-4)'
/** Opacidad de las lineas de contexto en la vista "Todas" (foco + contexto). */
export const DIM_OPACITY = 0.3
/** Guion del MARCADOR hueco (confianza del punto). Corto: el circulo mide ~2.8 de radio. */
export const LOWCONF_DASH = '2 1.5'
/** Guion del SEGMENTO (paso sin medicion nueva) y de las reglas verticales de cambio de canasta. */
export const CARRIED_DASH = '3 3'

/**
 * Color de la serie n-esima SIN modulo. El comentario historico de LINE_COLORS afirmaba que
 * `i % len` nunca envolvia porque habia "maximo 8 grupos": falso, MuscleGroup tiene 10 y un grupo
 * con 9 ejercicios tambien desborda. Envolver en silencio pinta DOS series del mismo color, que es
 * peor que no pintarlas: el usuario lee dos lineas como si fueran una. Al pasarse la paleta
 * degradamos a gris neutro, que se lee como "sin color propio" y empuja a usar el selector de
 * grupo. Indice negativo (imposible hoy, pero barato de blindar) toma la misma salida.
 */
export function colorFor(i: number): string {
  return i >= 0 && i < LINE_COLORS.length ? LINE_COLORS[i] : DIM_COLOR
}

/** Recorta un texto para que la caja del tooltip nunca se salga del viewBox (340u de ancho). */
export function clip(s: string, n = 24): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s
}

// ---------- marcas por punto ----------

/** Banderas de honestidad de UN punto, alineadas 1:1 con la serie dibujada. */
export interface PointMeta {
  lowConf?: boolean      // medicion poco fiable ese dia
  provisional?: boolean  // el paso se recorto por venir fuera de rango
  obs?: number           // miembros que votaron el paso (0 = nadie midio, valor arrastrado)
  rirImputed?: boolean   // el esfuerzo de la serie tope se estimo, no estaba anotado
}

export type MarkerKind = 'none' | 'hollow' | 'solid'

/**
 * Marca del punto i. Dos canales de guion que NO se pisan (regla del diseño auditado):
 * el guion del MARCADOR habla de la confianza del dato; el guion del SEGMENTO habla de si el
 * tramo se midio o se arrastro.
 *  - obs === 0 con i > 0: nadie midio en ese paso, el nivel viene arrastrado -> sin marcador
 *    (dibujar un punto solido ahi seria afirmar una medicion que no existe). El primer punto de
 *    la serie tambien trae obs 0 por construccion (es la base 100, no sale de ningun paso), por
 *    eso el indice entra en la decision: i === 0 SIEMPRE lleva marcador.
 *  - lowConf -> circulo HUECO (relleno del color de la tarjeta) con el aro discontinuo.
 *  - resto -> circulo relleno.
 */
export function markerKind(m: PointMeta | undefined, i: number): MarkerKind {
  if (i > 0 && m?.obs === 0) return 'none'
  if (m?.lowConf) return 'hollow'
  return 'solid'
}

/**
 * Fila de nota del tooltip para el punto i, o null si el punto esta limpio. Prioridad
 * provisional > lowConf > rirImputed > paso sin medicion: se muestra UNA sola nota, la mas
 * fuerte, porque el tooltip vive dentro de un viewBox de 172u de alto y apilar notas lo desborda.
 * Copy en espanol llano (sin RIR, sin e1RM, sin 1RM) y sin rayas, <= 44 caracteres por fila.
 */
export function noteFor(m: PointMeta | undefined, i = 0): string | null {
  if (!m) return null
  if (m.provisional) return 'dato fuera de rango, recortado'
  if (m.lowConf) return 'medición menos fiable ese día'
  if (m.rirImputed) return 'esfuerzo estimado, no anotado'
  if (i > 0 && m.obs === 0) return 'sin medición nueva ese día'
  return null
}

/** Un tramo de polilinea con estilo homogeneo. `idx` son indices de la serie, en orden. */
export interface Segment {
  dashed: boolean
  idx: number[]
}

/**
 * Parte la polilinea en tramos: un paso se dibuja PUNTEADO cuando nadie midio en el (obs === 0),
 * porque el nivel se arrastro y una linea continua afirmaria una medicion inexistente. Los pasos
 * consecutivos del mismo estilo se fusionan en un solo tramo (menos nodos en el DOM y sin costuras
 * visibles). `obs` undefined (lineas de ejercicio, que no son un indice encadenado) nunca puntea.
 * Con menos de 2 puntos no hay tramo que dibujar.
 */
export function segmentsOf(meta: ReadonlyArray<PointMeta | undefined>): Segment[] {
  if (meta.length < 2) return []
  const out: Segment[] = []
  for (let i = 1; i < meta.length; i++) {
    const dashed = meta[i]?.obs === 0
    const last = out[out.length - 1]
    if (last && last.dashed === dashed) last.idx.push(i)
    else out.push({ dashed, idx: [i - 1, i] })
  }
  return out
}

// ---------- eventos de canasta ----------

/**
 * Que le paso a la canasta en esa fecha:
 *  - 'grupo' / 'ejercicio': DEBUT, algo nuevo empieza a votar (entra con empalme a salto cero);
 *  - 'retiro': un miembro que venia votando deja de hacerlo por una pausa larga (STALE_DAYS);
 *  - 'reentrada': un miembro caducado vuelve a medirse y entra otra vez sin salto.
 * Las dos primeras nombran ADEMAS el nivel al que ocurrio el debut porque el desempate del orden lo
 * necesita; retiro y reentrada no lo necesitan (el texto de la fila los explica por si solos).
 */
export type BasketEventKind = 'grupo' | 'ejercicio' | 'retiro' | 'reentrada'

/** Un cambio de composicion del indice: algo entra, sale o vuelve a la canasta en esa fecha. */
export interface BasketEvent {
  date: string
  kind: BasketEventKind
  label: string
}

/** Dias enteros entre dos fechas ISO locales (a - b). Replica diffDays del nucleo: parseDay da
 *  medianoche LOCAL, y redondear hace la cuenta inmune a los saltos de horario de verano. */
const daysBetween = (aIso: string, bIso: string): number =>
  Math.round((parseDay(aIso).getTime() - parseDay(bIso).getTime()) / 86400000)

/** Orden de desempate dentro de una misma fecha: primero lo que entra, luego lo que vuelve, luego
 *  lo que sale. Es solo una convencion, pero tiene que ser FIJA para que la salida no dependa del
 *  orden de iteracion del modelo. */
const KIND_RANK: Record<BasketEventKind, number> = { grupo: 0, ejercicio: 1, reentrada: 2, retiro: 3 }

/**
 * Retiros y re-entradas de UN miembro a lo largo de los pasos de una cadena.
 *
 * POR QUE: el nucleo saca de la canasta al miembro cuya ultima medicion real queda a mas de
 * STALE_DAYS de la fecha del paso (strength.ts, buildGroupIndices / buildOverview). Ese miembro
 * deja de votar y, cuando vuelve, su primer punto NO vota tampoco: se empalma a salto cero (la
 * mejora acumulada durante la pausa no se cuenta, igual que un debut). Las dos cosas cambian lo que
 * el indice mide, asi que las dos merecen glifo y asterisco, no solo los debuts.
 *
 * Predicado replicado tal cual del nucleo: elegible <=> ya tiene una medicion real en o antes de
 * `prev` Y esa medicion esta a STALE_DAYS o menos de `cur` (45 dias entra, 46 sale).
 *
 * Dos detalles del diseño:
 *  - El cursor `c` avanza y nunca retrocede: las fechas de los pasos vienen en orden ascendente,
 *    asi que buscar la ultima observacion desde el principio en cada paso seria un re-escaneo inutil.
 *  - Cuando el paso que saca al miembro es JUSTO el dia en que vuelve (su punto existe pero aun no
 *    puede votar), anotar "sale" y "vuelve" en la misma fecha seria contradictorio: se anota solo la
 *    re-entrada, que es lo que el usuario ve en el grafico. Como las fechas de los pasos son la
 *    UNION de las fechas de todos los miembros, el dia en que un miembro vuelve SIEMPRE es un paso,
 *    asi que ninguna vuelta se escapa por este camino.
 */
function lifecycleOf(
  label: string,
  points: ReadonlyArray<{ date: string }>,
  dates: readonly string[],
): BasketEvent[] {
  const out: BasketEvent[] = []
  if (points.length === 0) return out
  let c = -1            // indice de la ultima observacion real en o antes de `prev` (-1 = aun no debuta)
  let inBasket = true   // un miembro esta en la canasta desde su debut y hasta que caduca
  for (let i = 1; i < dates.length; i++) {
    const prev = dates[i - 1], cur = dates[i]
    while (c + 1 < points.length && points[c + 1].date <= prev) c++
    if (c < 0) continue // todavia no ha debutado: su entrada la anota el evento de debut
    if (daysBetween(cur, points[c].date) <= STALE_DAYS) { inBasket = true; continue }
    if (points[c + 1]?.date === cur) { out.push({ date: cur, kind: 'reentrada', label }); inBasket = true; continue }
    if (inBasket) { out.push({ date: cur, kind: 'retiro', label }); inBasket = false }
  }
  return out
}

/**
 * Fechas en las que la CANASTA del indice cambio de composicion.
 *
 * POR QUE IMPORTA: el indice es encadenado (metodologia IPC), asi que un ejercicio o un grupo que
 * debuta entra con empalme a salto cero, sin mover el nivel. Eso es correcto, pero deja al usuario
 * comparando dos periodos cuyo contenido no es el mismo. Anotarlo es la unica forma honesta de
 * decirlo: "desde aqui el indice incluye X". Lo mismo vale para las SALIDAS por pausa larga y para
 * las vueltas, que tambien cambian lo que se esta midiendo (SF5/SF13/SF16).
 *
 * Reglas:
 *  - Debut de GRUPO: la fecha de su primer punto, salvo que sea el primer dia de toda la vista (en
 *    esa fecha debuta TODO por definicion, anotarlo seria ruido puro).
 *  - Debut de EJERCICIO: la fecha de su primer punto cuando es POSTERIOR al debut de su grupo (si
 *    coincide con el arranque del grupo ya esta contado por el evento del grupo).
 *  - Retiros y re-entradas de EJERCICIO: sobre los pasos del indice de su grupo (que son justo las
 *    fechas de sus puntos: la union de las fechas de sus ejercicios).
 *  - Retiros y re-entradas de GRUPO: solo cuando la llamada cubre la canasta general (mas de un
 *    grupo). Con un solo grupo la vista es su detalle, cuyos miembros son los ejercicios; el eje de
 *    grupo no es un nivel propio ahi, igual que su debut tampoco se anota.
 *
 * La llamada decide el alcance: basketEvents(groups) para la vista "Todas" y basketEvents([g])
 * para el detalle de un grupo. Salida ordenada por fecha, luego por KIND_RANK, luego alfabetica:
 * determinista, sin depender del orden de iteracion del modelo.
 */
export function basketEvents(groups: readonly GroupIndex[]): BasketEvent[] {
  const withPoints = groups.filter((g) => g.points.length > 0)
  if (withPoints.length === 0) return []
  const firstGlobal = withPoints.reduce((min, g) => (g.points[0].date < min ? g.points[0].date : min), withPoints[0].points[0].date)

  const out: BasketEvent[] = []
  const saidByGroup = new Map<string, Set<string>>() // grupo -> "tipo|fecha" ya anotados a su nivel
  if (withPoints.length > 1) {
    // Nivel general: los miembros son los GRUPOS y los pasos son la union de sus fechas, exactamente
    // como los recorre buildOverview.
    const dates = [...new Set(withPoints.flatMap((g) => g.points.map((p) => p.date)))].sort()
    for (const g of withPoints) {
      const evs = lifecycleOf(g.label, g.points, dates)
      saidByGroup.set(g.group, new Set(evs.map((e) => `${e.kind}|${e.date}`)))
      out.push(...evs)
    }
  }
  for (const g of withPoints) {
    const gDebut = g.points[0].date
    if (gDebut > firstGlobal) out.push({ date: gDebut, kind: 'grupo', label: g.label })
    const dates = g.points.map((p) => p.date) // ya ordenadas y sin repetir: son la union del grupo
    const said = saidByGroup.get(g.group)
    for (const ex of g.exercises) {
      if (ex.points.length === 0) continue
      const exDebut = ex.points[0].date
      if (exDebut > gDebut) out.push({ date: exDebut, kind: 'ejercicio', label: ex.name })
      // Misma regla que el debut: si el cambio ya esta contado a nivel de GRUPO, no se repite por
      // cada ejercicio. Pasa cuando un grupo entero vuelve tras una pausa larga: el ejercicio que lo
      // trae de vuelta diria exactamente lo mismo y el glifo listaria dos veces el mismo hecho.
      out.push(...lifecycleOf(ex.name, ex.points, dates).filter((e) => !said?.has(`${e.kind}|${e.date}`)))
    }
  }
  return out.sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1
      : a.kind !== b.kind ? KIND_RANK[a.kind] - KIND_RANK[b.kind]
        : a.label.localeCompare(b.label),
  )
}

/** Un grupo de eventos que caen tan cerca en el eje x que comparten un solo glifo. */
export interface EventCluster<T> {
  x: number
  events: T[]
}

/**
 * Colapsa los eventos que se pisarian en pantalla. En la historia real la densidad ronda 15u por
 * dia dentro de un viewBox de 340u, asi que dos debuts en dias contiguos producen glifos
 * solapados e imposibles de tocar por separado (objetivo tactil minimo ~24px).
 *
 * El corte es por ANCLA, no encadenado: un evento abre cluster y solo se le suman los que caen a
 * menos de `minPx` del ANCLA. Encadenar (comparar contra el ultimo) dejaria que una racha de
 * eventos arrastrara el cluster por medio grafico y el glifo terminaria lejos de los cambios que
 * representa. Con ancla, el ancho de un cluster es siempre < minPx. La x del cluster es la del
 * primer evento (el mas antiguo): el glifo marca "desde aqui", que es justo lo que dice el texto.
 */
export function clusterEvents<T extends { date: string }>(
  events: readonly T[],
  xOf: (date: string) => number,
  minPx: number,
): Array<EventCluster<T>> {
  const sorted = [...events].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
  const out: Array<EventCluster<T>> = []
  for (const e of sorted) {
    const x = xOf(e.date)
    const last = out[out.length - 1]
    if (last && x - last.x < minPx) last.events.push(e)
    else out.push({ x, events: [e] })
  }
  return out
}

/** Encabezado cuando en el glifo SOLO hay debuts (va como fila propia para no pasarse de 44). */
export const EVENT_LEAD = 'Desde aquí el índice incluye:'
/** Encabezado cuando el glifo mezcla entradas con salidas o vueltas: "incluye" seria mentira. */
export const EVENT_LEAD_MIXED = 'Desde aquí el índice cambia:'

/** Encabezado honesto para las filas de UN glifo, segun lo que traiga dentro. */
export function leadFor(events: readonly BasketEvent[]): string {
  return events.every((e) => e.kind === 'grupo' || e.kind === 'ejercicio') ? EVENT_LEAD : EVENT_LEAD_MIXED
}

/** Ancho maximo de una fila del tooltip: a 44 caracteres la caja mide ~261u de los 340 del viewBox
 *  (PADX*2 + 44 * 5.6), o sea sigue cabiendo entera con el margen de recorte de los bordes. */
const ROW_MAX = 44
/** Que dice cada fila segun el tipo de evento. En espanol llano, sin jerga de indice mas alla del
 *  "100" que el propio eje del grafico ya rotula. */
const ROW_SUFFIX: Record<BasketEventKind, string> = {
  grupo: '(entra en 100)',
  ejercicio: '(entra en 100)',
  retiro: '(sale por pausa larga)',
  reentrada: '(vuelve, entra en 100)',
}

/**
 * Filas del tooltip de un glifo. MAXIMO `max` filas de detalle mas una de resumen: sin este tope
 * un cluster de 9 debuts (perfectamente posible: en la historia real los debuts se apiñan en 4
 * fechas) generaria una caja mas alta que el propio grafico y a 390px taparia la pantalla entera.
 * Cada fila cabe en ROW_MAX caracteres, en espanol llano y sin rayas: el recorte del nombre se
 * DERIVA del sufijo (26 para los debuts, como siempre; 21 para los sufijos largos), asi que si
 * alguien cambia el texto las filas siguen cabiendo solas.
 */
export function eventRows(events: readonly BasketEvent[], max = 3): string[] {
  const rows = events.slice(0, max).map((e) => {
    const suffix = ROW_SUFFIX[e.kind]
    return `${clip(e.label, Math.min(26, ROW_MAX - suffix.length - 1))} ${suffix}`
  })
  const rest = events.length - max
  if (rest > 0) rows.push(`y ${rest} ${rest === 1 ? 'cambio más' : 'cambios más'}`)
  return rows
}
